import React, { useCallback, useEffect } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { mutations, useTeamPermissions } from '../api';
import { DocumentSourceEnum } from '../components/document-wizard/document-editor/constants/EditorConstats';
import { useNotifications } from '../notifications';
import { DocumentActionType, InviteReceivingTeamFormikProps, PermissionProps } from '../consts/forms-types';
import { SendDealErrorMessage } from '../consts/forms-constants';
import InviteReceivingTeamForm from './InviteReceivingTeamForm';
import { useDocumentWizardSuccessNotifier } from '../pages/document-wizard/shared/hooks/use-document-wizard-success-notifier/use-document-wizard-success-notifier.hook';
import { sendDealToReceiver } from '../utils/api/send-deal-to-receiver/send-deal-to-receiver';
import { Permissions } from '../shared/constants/permissions';
import { useDeal } from '../contexts/DealContext';
import { useDocumentState } from '../contexts/DocumentContext';
import { fillPlaceholders, paths } from '../routing';
import { saveDocumentVersionOnPossessionPass } from '../components/document-wizard/document-wizard-header/document-wizard-header.utils';
import { useDocumentRole } from '../shared/hooks/useDocumentRole/useDocumentRole';
import { CounterpartyPermissionLevel, permissionLevelToBooleans } from '../types/counterparty-permissions';
import { flushActiveEditor } from '../components/document-wizard/document-editor/flushActiveEditor';
import { shouldSendWithDocumentId } from './invite-receiving-team.utils';

// Whether someone may be the counterparty depends on THIS deal, not on the team
// they belong to: a colleague with no access to this deal can represent the
// other side of it. Only the server knows who holds access, so it owns that
// rule and its error surfaces through the submit handler.
const validationSchema = yup.object().shape({
  email: yup
    .string()
    .trim()
    .required('Email is required')
    .email('Please provide a valid email'),
  additionalMessage: yup.string(),
});

const InviteReceivingTeamFormik = ({
  bodyContainer,
  actionsContainer,
  renderSecondaryAction,
  sendDocumentToReceiverHandler,
  onSuccess,
  shouldAccessBeGranted,
  teamId,
  sendWithDocumentId = true,
  shareDocument = true,
  refreshCounterpartyData,
}: InviteReceivingTeamFormikProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { currentDeal, dealId, setCurrentDeal } = useDeal();
  const { state, updateDocumentState } = useDocumentState();
  const documentId = state.id as string;
  const documentSource = state.source;
  const isDocumentUploaded = documentSource === DocumentSourceEnum.IMPORTED;
  const { possessionTeamName } = useDocumentRole(documentId);
  const { teamPermissions } = useTeamPermissions();
  // Engine-agnostic editor readiness.
  //
  // This USED to read `window.editor?.state === 'ready'` — a global only
  // CKEditor ever set. After the DOCX-native migration it is permanently
  // undefined, so this was permanently false, and the branch in
  // handleSubmitAsync that attaches `documentId` to sendDealToReceiver never
  // fired. The backend only transitions the document (`request_document_review`
  // → status `reviewing`, possession + receiver team moved to the counterparty)
  // when a documentId actually arrives, so "Send → to counterparty" on a deal
  // with no counterparty invited them to the DEAL only and left the document in
  // DRAFT, still editable by the creator, with no document-level invite.
  //
  // `state.isEditorLoaded` is the engine-agnostic signal both editors set — the
  // same one DocumentWizardHeaderContainer and useSendDropdownLogic gate on.
  // Imported documents have no editor and are handled by `isDocumentUploaded`.
  const isEditorReady = documentSource === DocumentSourceEnum.APP && state.isEditorLoaded;
  const [, { error: notifyError }] = useNotifications();

  const apolloClient = useApolloClient();
  const notifySuccess = useDocumentWizardSuccessNotifier();
  const shouldDocumentBeShared = shareDocument && !!sendDocumentToReceiverHandler;
  const defaultPermissions = [];
  const viewPermissionId = teamPermissions?.find(({ key }: PermissionProps) => key === Permissions.View)?.id;
  const whitelistPermissionId = teamPermissions?.find(({ key }: PermissionProps) => key === Permissions.Whitelist)?.id;

  // Read unified permission level from document state (single source of truth)
  const counterpartyPermissionLevel = state.counterpartyPermissionLevel;

  // Unified mutation for permission updates (sync across all UI)
  const [setCounterpartyPermissionLevel] = useMutation(mutations.setCounterpartyPermissionLevel, {
    onCompleted: (data) => {
      const newValue = data.setCounterpartyPermissionLevel.counterpartyPermissionLevel;
      updateDocumentState({ counterpartyPermissionLevel: newValue, ...permissionLevelToBooleans(newValue) });
      // Emit custom event for WebViewer DOM dropdown sync
      window.dispatchEvent(new CustomEvent('propdocs:permissionChanged', {
        detail: { type: 'level', value: newValue }
      }));
    },
  });

  // Handler for immediate permission changes
  const handleCounterpartyPermissionChange = useCallback((level: CounterpartyPermissionLevel) => {
    if (documentId) {
      setCounterpartyPermissionLevel({ variables: { documentId, permissionLevel: level } });
    }
  }, [documentId, setCounterpartyPermissionLevel]);

  // Version snapshot for the possession pass.
  const [saveVersionMutation] = useMutation(mutations.saveDocumentVersion);
  const docxVersionArg = useCallback(() => {
    if (!documentId) return undefined;

    return {
      documentId: String(documentId),
      saveVersion: (name: string, event: string) =>
        saveVersionMutation({
          variables: { documentId: String(documentId), documentType: 'Document', name, event },
        }),
    };
  }, [documentId, saveVersionMutation]);

  const handleSubmitAsync = async (values: any) => {
    if (documentId && isEditorReady && !shouldAccessBeGranted) {
      // Land edits still inside the autosave debounce and reconcile the
      // relational suggestion rows before the server reads them. Replaces
      // `await handleSave` — which awaited a FUNCTION REFERENCE and so saved
      // nothing — and works for whichever engine is mounted.
      await flushActiveEditor(documentId);

      // Both snapshots are engine-aware: with no `docx` arg they fall through
      // to CKEditor's RevisionTracker, which does not exist under docx-native,
      // so sending from this form recorded no version at all and the party
      // without possession had nothing to fall back to.
      const docx = docxVersionArg();
      shouldDocumentBeShared &&
        (await saveDocumentVersionOnPossessionPass({
          success: true,
          possessionTeamName,
          sentForExternalReview: true,
          docx,
        }));
      await saveDocumentVersionOnPossessionPass({ success: true, possessionTeamName, docx });
    }

    const successShareDeal = intl.formatMessage({
      id: 'phrase.invitation_share.success',
      defaultMessage: `Success! Your deal has been shared with ${values.email}`,
    });

    const successMessageDealAndDocumentShare = intl.formatMessage({
      id: 'phrase.invitation_and_document_share.success',
      defaultMessage: 'Success! Your deal has been shared and the document has been sent for counterparty review.',
    });

    const successMessageDealAndDocumentGrantAccess = intl.formatMessage({
      id: 'phrase.invitation_send_and_document_grant_access.success',
      defaultMessage: 'Success! Your deal and document have been shared.',
    });

    const sendDealVariables = {
      dealId,
      receiverEmail: values.email,
      additionalMessage: values.additionalMessage,
      documentAction: shouldAccessBeGranted ? DocumentActionType.SHARE : DocumentActionType.REVIEW,
      // Use unified permission level from Redux (single source of truth, already persisted via immediate mutation)
      counterpartyPermissionLevel: counterpartyPermissionLevel,
    };

    return new Promise<void>((resolve, reject) => {
      setTimeout(
        async () => {
          try {
            const data = await sendDealToReceiver(
              shouldSendWithDocumentId({
                shouldAccessBeGranted,
                documentId,
                isDocumentUploaded,
                isEditorReady,
                sendWithDocumentId,
              })
                ? { ...sendDealVariables, documentId }
                : sendDealVariables
            )(apolloClient);

            if (currentDeal) {
              const updatedDeal = { ...currentDeal, receiverTeam: data, receiverEmail: values.email };
              setCurrentDeal(updatedDeal);
            }

            // Navigate FIRST (like existing counterparty flow)
            if (teamId && sendWithDocumentId) {
              const dealDeatilsUrl = fillPlaceholders(paths.deals.detail, {
                teamId,
                dealId,
              });
              navigate(dealDeatilsUrl);
            }

            // FIRST: Call onSuccess to trigger data refetch
            await onSuccess?.();

            // THEN: Show success toaster AFTER refetch completes
            setTimeout(() => {
              if (documentId && shareDocument) {
                const messageToShow = shouldAccessBeGranted
                  ? successMessageDealAndDocumentGrantAccess
                  : successMessageDealAndDocumentShare;
                notifySuccess(messageToShow);
              } else if (!documentId || !shareDocument) {
                notifySuccess(successShareDeal);
              }
            }, 100);

            refreshCounterpartyData();
            resolve();
          } catch (error: any) {
            saveDocumentVersionOnPossessionPass({ success: false });
            notifyError({ message: { id: 'deal.send-deal.error', defaultMessage: error.message || SendDealErrorMessage.SEND_DEAL } });
            reject(error);
          }
        },
        shouldDocumentBeShared ? 2000 : 0
      );
    });
  };

  const onSubmit = async (values: any) => {
    return await handleSubmitAsync(values);
  };

  if (viewPermissionId) defaultPermissions.push(viewPermissionId);
  if (whitelistPermissionId) defaultPermissions.push(whitelistPermissionId);

  return (
    <Formik
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      initialValues={{
        email: '',
        // Initialize from Redux (unified permission level)
        counterpartyPermissionLevel: counterpartyPermissionLevel || 'COMMENT_ONLY',
      }}
      // REMOVED enableReinitialize to prevent email field from clearing when dropdown changes
    >
      {(formikProps) => {
        // Sync Redux state to Formik field value WITHOUT resetting email field
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          const currentLevel = counterpartyPermissionLevel || 'COMMENT_ONLY';
          if (formikProps.values.counterpartyPermissionLevel !== currentLevel) {
            formikProps.setFieldValue('counterpartyPermissionLevel', currentLevel);
          }
        }, [counterpartyPermissionLevel]);

        return (
          <InviteReceivingTeamForm
            {...formikProps}
            bodyContainer={bodyContainer}
            actionsContainer={actionsContainer}
            renderSecondaryAction={renderSecondaryAction}
            shouldDocumentBeShared={shouldDocumentBeShared}
            shouldAccessBeGranted={shouldAccessBeGranted}
            isImportedPdf={isDocumentUploaded}
            onCounterpartyPermissionChange={handleCounterpartyPermissionChange}
          />
        );
      }}
    </Formik>
  );
};

export default InviteReceivingTeamFormik;
